/**
 * Pairing of a customer order's cargo line (ZK) with the ZD line it comes
 * from, on a „Fabryka → klient” truck (decision
 * docs/decisions/2026-10-06-auto-zaladunek-rozladunek-parowanie.md, PR3).
 * The form's rules, quantities only:
 * - a ZK line pairs only with a ZD line on this truck of the same variant;
 *   the operator's choice wins, then the saved pair, then the only match;
 * - the default ZD quantity on the truck = Σ ZK quantities paired with it
 *   (capped at what is still free of the line), until the operator types one;
 *   the other lines of that ZD keep what they carried (keepUntouchedSupplierCargoLines).
 * The server validates and warns (backend utils/transportCargoPairing.js);
 * the freight split is not here.
 */
import {
  getQuantitySetValue,
  resolveStoredQuantitySet,
  roundCommercialQuantity,
  scaleQuantitySetToAmount,
  subtractQuantitySets,
  type QuantitySet,
} from '../../utils/commercialQuantity';
import { commonGuessUnit, GUESS_UNITS, guessUnpairedFills, type GuessSets, type GuessUnit } from './unpairedCargoGuess';
import {
  resolveSupplierCargoLines,
  setValue,
  type SupplierCargoEntry,
  type SupplierCargoLine,
  type SupplierCargoUnit,
} from './supplierTruckCargo';

export interface PairableCargoRow {
  key: string;
  /** ZK of the row (cargo rows carry it); tells „other customers” apart. */
  orderId?: string;
  orderProductId?: string;
  productVariantId?: string | null;
  quantity?: number | null;
  unit?: string | null;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
}

export interface CargoPair {
  /** The ZD line this ZK line comes from; null = no pair. */
  lineId: string | null;
  /** ZD lines on this truck with the same variant. */
  candidates: SupplierCargoLine[];
  /** Set automatically (the only match), not chosen nor saved. */
  auto: boolean;
}

export function pairCandidates(row: PairableCargoRow, lines: SupplierCargoLine[]): SupplierCargoLine[] {
  if (!row.productVariantId) return [];
  return lines.filter((line) => line.productVariantId === row.productVariantId);
}

/**
 * @param chosen operator choices by cargo row key (null = cleared)
 * @param saved  saved pairs by cargo row key (edit mode)
 */
export function resolveCargoPairs(
  rows: PairableCargoRow[],
  lines: SupplierCargoLine[],
  chosen: Record<string, string | null>,
  saved: ReadonlyMap<string, string | null>,
  selectableLineIds?: ReadonlySet<string>,
): Map<string, CargoPair> {
  const lineIds = new Set(lines.map((line) => line.itemId));
  return new Map(rows.map((row): [string, CargoPair] => {
    const candidates = pairCandidates(row, lines).filter((line) => !selectableLineIds || selectableLineIds.has(line.itemId));
    if (Object.prototype.hasOwnProperty.call(chosen, row.key)) {
      const value = chosen[row.key];
      return [row.key, { lineId: value && lineIds.has(value) && (!selectableLineIds || selectableLineIds.has(value) || saved.get(row.key) === value) ? value : null, candidates, auto: false }];
    }
    const stored = saved.get(row.key);
    if (stored && lineIds.has(stored)) return [row.key, { lineId: stored, candidates, auto: false }];
    if (candidates.length === 1) return [row.key, { lineId: candidates[0].itemId, candidates, auto: true }];
    return [row.key, { lineId: null, candidates, auto: false }];
  }));
}

/** The row's quantity in the ZD line's unit; null = cannot be expressed. */
function rowAmountInLineUnit(row: PairableCargoRow, line: SupplierCargoLine): number | null {
  if (!line.unit) return null;
  if (row.unit === line.unit) return Number(row.quantity) || 0;
  return getQuantitySetValue(resolveStoredQuantitySet(row as unknown as Record<string, unknown>), line.unit);
}

/** Σ loaded ZK quantities per paired ZD line, in the line's unit (null = unknown). */
export function pairedQuantityByLine(
  loadedRows: PairableCargoRow[],
  pairs: ReadonlyMap<string, CargoPair>,
  lines: SupplierCargoLine[],
): Map<string, number | null> {
  const lineById = new Map(lines.map((line) => [line.itemId, line]));
  const sums = new Map<string, number | null>();
  loadedRows.forEach((row) => {
    const lineId = pairs.get(row.key)?.lineId;
    const line = lineId ? lineById.get(lineId) : undefined;
    if (!line) return;
    const amount = rowAmountInLineUnit(row, line);
    const previous = sums.has(line.itemId) ? sums.get(line.itemId)! : 0;
    sums.set(line.itemId, amount === null || previous === null ? null : roundCommercialQuantity(previous + amount));
  });
  return sums;
}

/**
 * Default ZD quantities of a factory → customer truck: Σ paired ZK, capped at
 * what the other trucks leave of the line. Lines the operator typed (or a
 * saved explicit quantity) keep their value and get no default.
 */
export function deriveSupplierCargoEntries(
  lines: SupplierCargoLine[],
  paired: ReadonlyMap<string, number | null>,
  operatorEntries: Record<string, SupplierCargoEntry>,
): Record<string, SupplierCargoEntry> {
  const result: Record<string, SupplierCargoEntry> = {};
  lines.forEach((line) => {
    if (!line.unit || operatorEntries[line.itemId] || line.savedExplicit) return;
    const sum = paired.get(line.itemId);
    if (sum === null || sum === undefined || !(sum > 0)) return;
    const free = line.otherExplicit === null
      ? null
      : setValue(subtractQuantitySets(line.ordered, line.otherExplicit), line.unit);
    const quantity = free === null ? sum : Math.min(sum, Math.max(free, 0));
    if (quantity > 0) result[line.itemId] = { quantity: roundCommercialQuantity(quantity), unit: line.unit };
  });
  return result;
}

/**
 * The other lines of a generated ZD with a customer sum (task 2026-10-14: one ZD, two
 * lines, two ZK): a sum lists the ZD on this truck, which would drop its
 * lines without customers to „nie jedzie tym autem” although nobody chose
 * that. They keep what they carried before (rule 2026-10-05, as
 * `applySupplierCargoEntry`), so their goods stay on the board to be assigned
 * and, unassigned, ride to the warehouse (decision 2026-10-10 pt 2). A line
 * whose rest another truck takes, typed, saved or „Nic” lines get nothing.
 */
export function keepUntouchedSupplierCargoLines(
  lines: SupplierCargoLine[],
  customerSums: Record<string, SupplierCargoEntry>,
  operatorEntries: Record<string, SupplierCargoEntry>,
  directDeliveryLineIds: ReadonlySet<string>,
): Record<string, SupplierCargoEntry> {
  const eligibleLines = lines.filter((line) => directDeliveryLineIds.has(line.itemId));
  const summedZdIds = new Set(eligibleLines
    .filter((line) => customerSums[line.itemId] && !operatorEntries[line.itemId])
    .map((line) => line.supplierOrderId));
  const states = resolveSupplierCargoLines(lines, operatorEntries);
  const kept: Record<string, SupplierCargoEntry> = {};
  eligibleLines
    .filter((line) => summedZdIds.has(line.supplierOrderId) && line.unit && line.otherRestTrucks === 0)
    .filter((line) => !customerSums[line.itemId] && !operatorEntries[line.itemId] && !line.savedExplicit)
    .forEach((line) => {
      const amount = setValue(states.get(line.itemId)?.carried ?? null, line.unit);
      if (amount !== null && amount > 0) kept[line.itemId] = { quantity: amount, unit: line.unit as SupplierCargoUnit };
    });
  return kept;
}

/**
 * Where loaded ZK lines WITHOUT a pair are counted (review 2026-10-07): a
 * missing pair is a warning, not a block (decision pt 4), but the goods still
 * ride a ZD line, so the default ZD quantity — and with it the freight split
 * between the ZD — must include them. The guess is the truck card's own
 * („do przypisania”, unpairedCargoGuess — one home for both), on what each line
 * really carries here: the operator's entry, else the saved quantity, else what
 * the other trucks leave; minus the paired rows. Nothing is saved as a pair.
 *
 * @returns per ZD line the guessed amount in the line's unit, and per cargo
 *          row key the lines it was counted on.
 */
export function guessUnpairedQuantities(
  loadedRows: PairableCargoRow[],
  pairs: ReadonlyMap<string, CargoPair>,
  lines: SupplierCargoLine[],
  operatorEntries: Record<string, SupplierCargoEntry>,
): { byLine: Map<string, number>; linesByRow: Map<string, string[]> } {
  const toSets = (set: QuantitySet | null): GuessSets => ({ pcs: Number(set?.pcs) || 0, m2: Number(set?.m2) || 0, m3: Number(set?.m3) || 0 });
  const capacity = new Map(lines.map((line) => {
    const entry = operatorEntries[line.itemId];
    const explicit = entry ? (entry.quantity === null ? null : entry) : line.savedExplicit;
    const base = explicit && explicit.quantity !== null
      ? scaleQuantitySetToAmount(line.ordered, explicit.quantity, explicit.unit)
      : (line.otherExplicit === null ? line.ordered : subtractQuantitySets(line.ordered, line.otherExplicit));
    return [line.itemId, toSets(base)];
  }));
  // Paired rows first, as on the card: each takes its part of its line.
  loadedRows.forEach((row) => {
    const lineId = pairs.get(row.key)?.lineId;
    const line = lineId ? lines.find((candidate) => candidate.itemId === lineId) : undefined;
    const remaining = line ? capacity.get(line.itemId) : undefined;
    if (!line || !remaining) return;
    const need = toSets(resolveStoredQuantitySet(row as unknown as Record<string, unknown>));
    const unit = commonGuessUnit(line.unit as GuessUnit | null, need, remaining);
    if (!unit) return;
    const ratio = Math.min(need[unit], remaining[unit]) / remaining[unit];
    GUESS_UNITS.forEach((other) => { remaining[other] = Math.max(0, remaining[other] * (1 - ratio)); });
  });
  const unpaired = loadedRows.filter((row) => !pairs.get(row.key)?.lineId && row.productVariantId);
  const keyOf = (row: PairableCargoRow) => row.orderProductId || row.key;
  const { fills } = guessUnpairedFills(
    unpaired.map((row) => ({
      key: keyOf(row),
      variantId: row.productVariantId,
      need: toSets(resolveStoredQuantitySet(row as unknown as Record<string, unknown>)),
    })),
    lines.map((line) => ({
      id: line.itemId, variantId: line.productVariantId, unit: line.unit as GuessUnit | null, remaining: capacity.get(line.itemId)!,
    })),
  );
  const byLine = new Map<string, number>();
  const linesByRow = new Map<string, string[]>();
  unpaired.forEach((row) => {
    const rowFills = fills.get(keyOf(row)) || [];
    if (rowFills.length > 0) linesByRow.set(row.key, rowFills.map((fill) => fill.lineId));
    rowFills.forEach((fill) => {
      const line = lines.find((candidate) => candidate.itemId === fill.lineId);
      const unit = (line?.unit || fill.unit) as GuessUnit;
      byLine.set(fill.lineId, roundCommercialQuantity((byLine.get(fill.lineId) ?? 0) + fill.taken[unit]));
    });
  });
  return { byLine, linesByRow };
}

/** Σ paired + guessed per ZD line (an unknown paired sum stays unknown). */
export function addGuessedQuantities(
  paired: ReadonlyMap<string, number | null>,
  guessed: ReadonlyMap<string, number>,
): Map<string, number | null> {
  const sums = new Map(paired);
  guessed.forEach((amount, lineId) => {
    const previous = sums.has(lineId) ? sums.get(lineId)! : 0;
    sums.set(lineId, previous === null ? null : roundCommercialQuantity(previous + amount));
  });
  return sums;
}

/**
 * Production 2026-10-22 (TR-ZK-2/09/2026-1): a new factory → customer truck
 * saved 400 szt. of the ZD and 400 + 475 + 475 szt. for the ZK, because the ZK
 * rows nobody touched kept their full default after the ZD lines were set to
 * 400 and „Nic”. On a new truck the untouched ZK rows take only what the ZD
 * side brings of their variant: typed rows count first, then the rows a
 * drop wrote (`firstKeys`), then the untouched ones in row order; what is
 * left without goods gets 0 („Nic”). A ZD line that is still the Σ of its
 * ZK rows (`derivedLineIds`) brings what the other trucks leave of it, not
 * its current Σ — so the pool does not depend on the rows it caps, and
 * „Wszystko” on that line after „Nic” on the others keeps ZK ≤ ZD (review
 * PR #431, H1). No ZD line yet (none chosen) changes nothing; a variant no
 * ZD line on the truck has starts at 0, unless a ZD line has no variant
 * (its goods are unknown, review PR #431, M1).
 *
 * @param defaults untouched row key → its default quantity (row unit)
 * @param carried  per ZD line what this truck carries (resolveSupplierCargoLines)
 * @param firstKeys rows of `defaults` served before the others (drop-written)
 * @returns untouched row key → quantity to show
 */
export function capUntouchedRowsToSupplierCargo(
  rows: PairableCargoRow[],
  defaults: ReadonlyMap<string, number>,
  lines: SupplierCargoLine[],
  carried: ReadonlyMap<string, QuantitySet | null | undefined>,
  derivedLineIds: ReadonlySet<string>,
  firstKeys: ReadonlySet<string> = new Set(),
): Map<string, number> {
  return planSupplierCargoCap(rows, defaults, lines, carried, derivedLineIds, firstKeys).quantities;
}

/**
 * `capUntouchedRowsToSupplierCargo` with the reason a row got less (NZ.5,
 * owner 2026-10-22: „po prostu informacja”): `reducedByOthers` = rows that
 * alone would get their default, but other customers on this truck took the
 * goods first (row key → its default). A row the ZD side simply does not
 * bring enough for is not in it — „na tym aucie X z Y” already says so.
 */
export function planSupplierCargoCap(
  rows: PairableCargoRow[],
  defaults: ReadonlyMap<string, number>,
  lines: SupplierCargoLine[],
  carried: ReadonlyMap<string, QuantitySet | null | undefined>,
  derivedLineIds: ReadonlySet<string>,
  firstKeys: ReadonlySet<string> = new Set(),
): { quantities: Map<string, number>; reducedByOthers: Map<string, number> } {
  const result = new Map(defaults);
  const reducedByOthers = new Map<string, number>();
  if (lines.length === 0) return { quantities: result, reducedByOthers };
  const units = ['pcs', 'm2', 'm3'] as const;
  const free = (line: SupplierCargoLine): QuantitySet => (line.otherExplicit === null
    ? line.ordered
    : Object.fromEntries(units.map((unit) => [unit, line.ordered[unit] === null
      ? null
      : Math.max(0, Number(line.ordered[unit]) - (Number(line.otherExplicit![unit]) || 0))])) as unknown as QuantitySet);
  const linesWithoutVariant = lines.some((line) => !line.productVariantId);
  const variants = new Set(rows.map((row) => row.productVariantId).filter((id): id is string => Boolean(id)));
  variants.forEach((variantId) => {
    const variantLines = lines.filter((line) => line.productVariantId === variantId);
    if (variantLines.length === 0) {
      if (linesWithoutVariant) return;
      rows.forEach((row) => { if (row.productVariantId === variantId && defaults.has(row.key)) result.set(row.key, 0); });
      return;
    }
    const sets = variantLines.map((line) => (derivedLineIds.has(line.itemId) ? free(line) : carried.get(line.itemId)));
    if (sets.some((set) => !set)) return;
    // A unit no line knows stays unknown (null), not 0.
    const remaining = Object.fromEntries(units.map((unit) => [unit, sets.every((set) => set![unit] === null)
      ? null
      : sets.reduce((sum, set) => sum + (Number(set![unit]) || 0), 0)])) as unknown as QuantitySet;
    const initial = { ...remaining };
    const valueIn = (set: QuantitySet, unit: string | null | undefined): number | null => (
      unit && (units as readonly string[]).includes(unit) ? getQuantitySetValue(set, unit as (typeof units)[number]) : null);
    // ZK that already took goods of this variant (before the row at hand).
    const takers = new Set<string>();
    // Takes `amount` (row unit) off the variant's goods; null = not expressible.
    const take = (unit: string | null | undefined, amount: number): number | null => {
      const available = valueIn(remaining, unit);
      if (available === null) return null;
      const taken = Math.max(Math.min(amount, available), 0);
      const ratio = available > 0 ? taken / available : 0;
      units.forEach((key) => {
        if (remaining[key] !== null) remaining[key] = Math.max(0, Number(remaining[key]) * (1 - ratio));
      });
      return roundCommercialQuantity(taken);
    };
    const variantRows = rows.filter((row) => row.productVariantId === variantId);
    const orderOf = (row: PairableCargoRow) => row.orderId ?? row.key;
    variantRows.filter((row) => !defaults.has(row.key)).forEach((row) => {
      if ((take(row.unit, Number(row.quantity) || 0) ?? 0) > 0) takers.add(orderOf(row));
    });
    const capped = variantRows.filter((row) => defaults.has(row.key));
    [...capped.filter((row) => firstKeys.has(row.key)), ...capped.filter((row) => !firstKeys.has(row.key))].forEach((row) => {
      const wanted = defaults.get(row.key)!;
      const othersTook = Array.from(takers).some((orderId) => orderId !== orderOf(row));
      const taken = take(row.unit, wanted);
      if (taken === null) return;
      result.set(row.key, taken);
      if (taken > 0) takers.add(orderOf(row));
      const alone = valueIn(initial, row.unit);
      if (othersTook && alone !== null && alone >= wanted && taken < wanted) reducedByOthers.set(row.key, wanted);
    });
  });
  return { quantities: result, reducedByOthers };
}
